import { useEffect, useState } from "react";
import { Check, X, Trash2, LogOut, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/reviews";
import { Stars } from "@/components/shop/ProductCard";
import { ReviewPhotos } from "@/components/shop/ReviewPhotos";
import { BeforeAfterAdmin } from "@/components/shop/BeforeAfterAdmin";
import { InstagramAdmin } from "@/components/shop/InstagramAdmin";

const TABS = [["pending", "Na čekanju"], ["approved", "Odobrene"], ["rejected", "Odbijene"]];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try { onLogin(await adminApi.login(email, password)); } catch (x) { setErr(apiError(x)); }
  };
  const input = "w-full h-12 bg-transparent border border-white/15 px-4 text-sm focus:outline-none focus:border-[#c9a55c]";
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="max-w-sm mx-auto py-24 space-y-4">
      <p className="eyebrow">Admin</p>
      <h1 className="font-display text-4xl mb-4">Prijava</h1>
      <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={input} />
      <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Lozinka" className={input} />
      {err && <p data-testid="admin-login-error" className="text-xs text-red-400">{err}</p>}
      <button data-testid="admin-login-button" className="btn-gold w-full h-12">Prijavi se</button>
    </form>
  );
};

const Row = ({ r, act }) => (
  <div data-testid={`admin-review-${r.id}`} className="border border-white/10 p-5 flex flex-col md:flex-row gap-4 md:items-start">
    <div className="flex-1">
      <div className="flex items-center gap-3 mb-2"><Stars value={r.rating} /><span className="text-xs text-white/40">{new Date(r.created_at).toLocaleString("hr-HR")}</span></div>
      <p className="text-sm text-gold">{r.product_title || r.product_handle}</p>
      <p className="mt-2 mb-3 text-white/85">“{r.text}”</p>
      <ReviewPhotos photos={r.photos} testid={`admin-review-photos-${r.id}`} />
      <p className="text-xs text-white/50 mt-2">{r.name}{r.email ? ` · ${r.email}` : ""}</p>
    </div>
    <div className="flex gap-2 shrink-0">
      {r.status !== "approved" && <button data-testid={`admin-approve-${r.id}`} onClick={() => act(r.id, "approved")} className="btn-gold h-10 px-4 flex items-center gap-2"><Check size={14} /> Odobri</button>}
      {r.status === "pending" && <button data-testid={`admin-reject-${r.id}`} onClick={() => act(r.id, "rejected")} className="btn-ghost h-10 px-4 flex items-center gap-2"><X size={14} /> Odbij</button>}
      {r.status === "approved" && <button data-testid={`admin-unpublish-${r.id}`} onClick={() => act(r.id, "pending")} className="btn-ghost h-10 px-4 flex items-center gap-2"><RotateCcw size={14} /> Skini</button>}
      <button data-testid={`admin-delete-${r.id}`} onClick={() => act(r.id, "delete")} className="h-10 w-10 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-400" aria-label="Obriši"><Trash2 size={14} /></button>
    </div>
  </div>
);

const PasswordChange = () => {
  const [f, setF] = useState({ current: "", next: "", repeat: "" });
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return setMsg({ err: true, text: "Nova lozinka mora imati najmanje 8 znakova." });
    if (f.next !== f.repeat) return setMsg({ err: true, text: "Nove lozinke se ne podudaraju." });
    try {
      await adminApi.changePassword(f.current, f.next);
      setMsg({ err: false, text: "Lozinka je promijenjena." });
      setF({ current: "", next: "", repeat: "" });
    } catch (x) {
      const d = apiError(x);
      setMsg({ err: true, text: d === "Current password is incorrect" ? "Trenutna lozinka nije točna." : d === "New password must be different" ? "Nova lozinka mora biti drugačija od trenutne." : d });
    }
  };
  const input = "w-full h-11 bg-transparent border border-white/15 px-4 text-sm focus:outline-none focus:border-[#c9a55c]";
  return (
    <form onSubmit={submit} data-testid="admin-password-form" className="border border-white/10 p-6 mt-14 max-w-md space-y-3">
      <h2 className="font-display text-2xl mb-2">Promjena lozinke</h2>
      <input data-testid="admin-current-password-input" type="password" required value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} placeholder="Trenutna lozinka" className={input} />
      <input data-testid="admin-new-password-input" type="password" required value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} placeholder="Nova lozinka (min. 8 znakova)" className={input} />
      <input data-testid="admin-repeat-password-input" type="password" required value={f.repeat} onChange={(e) => setF({ ...f, repeat: e.target.value })} placeholder="Ponovi novu lozinku" className={input} />
      {msg && <p data-testid={msg.err ? "admin-password-error" : "admin-password-success"} className={`text-xs ${msg.err ? "text-red-400" : "text-gold"}`}>{msg.text}</p>}
      <button data-testid="admin-password-submit-button" className="btn-gold h-11 px-6">Spremi novu lozinku</button>
    </form>
  );
};

const Inquiries = () => {
  const [list, setList] = useState([]);
  useEffect(() => { adminApi.inquiries().then(setList).catch(() => {}); }, []);
  return (
    <div data-testid="admin-inquiries" className="mt-14">
      <h2 className="font-display text-2xl mb-4">Poslovni upiti <span className="text-white/40 text-base">{list.length}</span></h2>
      <div className="space-y-3">
        {list.map((q) => (
          <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="border border-white/10 p-5 text-sm">
            <p className="text-gold">{q.company || q.name} <span className="text-white/40 text-xs ml-2">{new Date(q.created_at).toLocaleString("hr-HR")}</span></p>
            <p className="text-white/60 text-xs mt-1">{q.name} · <a href={`mailto:${q.email}`} className="underline">{q.email}</a>{[q.phone, q.country].filter(Boolean).map((x) => ` · ${x}`).join("")}</p>
            {(q.business_type || q.volume || q.brand) && <p data-testid={`admin-inquiry-meta-${q.id}`} className="text-white/50 text-xs mt-1">{[{ distributor: "Distributer / veletrgovac", salon: "Salon / spa", retail: "Trgovina", pharmacy: "Ljekarna", hotel: "Hotel", online: "Web trgovina", other: "Ostalo" }[q.business_type] || q.business_type, { v1: "do 1.000 €", v2: "1.000–5.000 €", v3: "5.000–20.000 €", v4: "više od 20.000 €" }[q.volume] || q.volume, { primus: "Primus", bellecare: "Bellecare Natural", both: "Oba brenda", other: "Drugi europski brendovi", private: "Vlastiti brend" }[q.brand] || q.brand].filter(Boolean).join(" · ")}</p>}
            <p className="mt-3 text-white/85 whitespace-pre-wrap">{q.message}</p>
          </div>
        ))}
        {!list.length && <p className="text-white/40 text-sm">Nema upita.</p>}
      </div>
    </div>
  );
};

const Moderation = ({ onLogout }) => {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ reviews: [], counts: {} });
  const load = () => adminApi.list(tab).then(setData).catch((e) => { if (e.response?.status === 401) onLogout(); });
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id, status) => {
    try {
      if (status === "delete") await adminApi.remove(id); else await adminApi.setStatus(id, status);
      toast.success(status === "approved" ? "Recenzija objavljena" : "Spremljeno");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="admin-moderation" className="max-w-5xl mx-auto px-5 py-14">
      <div className="flex items-center justify-between mb-8">
        <div><p className="eyebrow mb-2">Admin</p><h1 className="font-display text-4xl">Recenzije kupaca</h1></div>
        <button data-testid="admin-logout-button" onClick={async () => { await adminApi.logout(); onLogout(); }} className="btn-ghost h-10 px-4 flex items-center gap-2"><LogOut size={14} /> Odjava</button>
      </div>
      <div className="flex gap-2 mb-6">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 h-10 text-xs uppercase tracking-[0.15em] border ${tab === k ? "bg-gold text-[#0b0a08] border-transparent" : "border-white/15 text-white/70"}`}>
            {l} <span className="opacity-60">{data.counts[k] ?? ""}</span>
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {data.reviews.map((r) => <Row key={r.id} r={r} act={act} />)}
        {!data.reviews.length && <p data-testid="admin-empty" className="text-white/40 text-sm py-10">Nema recenzija.</p>}
      </div>
      <BeforeAfterAdmin />
      <InstagramAdmin />
      <Inquiries />
      <PasswordChange />
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => { adminApi.me().then(setUser).catch(() => setUser(false)); }, []);
  if (user === null) return <div className="py-32" />;
  return user ? <Moderation onLogout={() => setUser(false)} /> : <Login onLogin={setUser} />;
}
